<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex flex-wrap justify-between items-center gap-2 max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">จัดการชาวบ้าน</h1>
        <div class="flex flex-wrap items-center gap-2">
          <TemplateExportButton
            collection-name="gameVillagers"
            file-name="gameVillagers.json"
            label="Export gameVillagers.json"
          />
        <button 
          @click="toggleModal = 'AddVillagerModal'" 
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="เพิ่มชาวบ้านใหม่"
        >
          <i class="fas fa-plus-circle text-xs"></i> เพิ่มชาวบ้านใหม่
        </button>
        </div>
      </div>
    </header>

    <!-- Tabs -->
    <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex space-x-4">
        <button 
          @click="setActiveTab('filter')" 
          :class="{'bg-indigo-600': activeTab === 'filter', 'bg-gray-700': activeTab !== 'filter'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          ตัวกรองชาวบ้าน
        </button>
        <button 
          @click="setActiveTab('recent')" 
          :class="{'bg-indigo-600': activeTab === 'recent', 'bg-gray-700': activeTab !== 'recent'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          ชาวบ้านเพิ่มล่าสุด
        </button>
      </div>
    </div>

    <!-- Filters Tab -->
    <section v-if="activeTab === 'filter'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ตัวกรองชาวบ้าน</h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Location (Required) -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">เลือกสถานที่ (จำเป็น)</label>
            <select 
              v-model="filterLocation" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกสถานที่ชาวบ้าน"
            >
              <option value="">เลือกสถานที่</option>
              <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
            </select>
          </div>

          <!-- Type Filter -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">ประเภทชาวบ้าน</label>
            <select 
              v-model="filterType" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกประเภทชาวบ้าน"
              :disabled="!filterLocation"
            >
              <option value="">ทั้งหมด</option>
              <option value="นักรบรับจ้าง">นักรบรับจ้าง</option>
            </select>
          </div>

          <!-- Name Search -->
          <div class="relative col-span-2 sm:col-span-1">
            <label class="block text-sm font-medium text-indigo-200 mb-1">ค้นหาชื่อชาวบ้าน</label>
            <i class="fas fa-magnifying-glass absolute left-3 top-10 text-indigo-400"></i>
            <input 
              v-model="filterName" 
              @input="debouncedApplyFilters" 
              type="text" 
              placeholder="ค้นหาชื่อชาวบ้าน..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              :disabled="!filterLocation"
              aria-label="ค้นหาชื่อชาวบ้าน"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Recent Villagers Tab -->
    <section v-if="activeTab === 'recent'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ชาวบ้านที่เพิ่มล่าสุด</h3>
        <!-- Filters for Recent -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">ประเภทชาวบ้าน</label>
            <select 
              v-model="filterRecentType" 
              @change="applyRecentFilters"
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกประเภทชาวบ้าน"
            >
              <option value="">ทั้งหมด</option>
              <option value="นักรบรับจ้าง">นักรบรับจ้าง</option>
            </select>
          </div>
          <div class="relative">
            <label class="block text-sm font-medium text-indigo-200 mb-1">ค้นหาชื่อชาวบ้าน</label>
            <i class="fas fa-magnifying-glass absolute left-3 top-10 text-indigo-400"></i>
            <input 
              v-model="filterRecentName" 
              type="text" 
              placeholder="ค้นหาชื่อชาวบ้าน..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="ค้นหาชื่อชาวบ้าน"
            />
          </div>
        </div>
        <!-- No Villagers Message -->
        <div v-if="displayedRecentVillagers.length === 0 && !isRecentLoading" class="text-center text-gray-300 py-10">
          <div class="mx-auto w-16 h-16 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-3">
            <i class="fas fa-users-slash text-gray-500"></i>
          </div>
          <div class="text-lg text-gray-200 mb-2">ไม่พบชาวบ้านที่เพิ่มล่าสุด</div>
          <div class="text-sm text-gray-400 mb-4">ยังไม่มีชาวบ้านใหม่ที่ถูกเพิ่มเข้ามา</div>
        </div>
        <!-- Grid of Villagers -->
        <div v-else class="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
          <div 
            v-for="villager in displayedRecentVillagers" 
            :key="villager.id" 
            class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition cursor-pointer"
            @click="openDetailsModal(villager)"
          >
            <!-- Header: Image + Name + Level -->
            <div class="p-3 flex items-center">
              <div class="relative">
                <img 
                  :src="villager.img || 'default-villager.png'" 
                  alt="Villager Image" 
                  class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                  @error="handleImageError"
                />
              </div>
              <div class="ml-3 flex-1">
                <div class="flex items-center justify-between">
                  <div class="font-semibold text-gray-100 truncate">
                    {{ villager.name }}
                  </div>
                </div>
                <div class="flex items-center space-x-1 px-1">
                  <!-- ชิปบุก -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                    <i class="fas fa-dice text-red-300 text-xs"></i>
                    <span class="font-mono">{{ getDice(villager, 'attackDice') }}</span>
                  </div>
                  <!-- ชิปตั้งรับ -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                    <i class="fas fa-dice text-blue-300 text-xs"></i>
                    <span class="font-mono">{{ getDice(villager, 'defenseDice') }}</span>
                  </div>
                </div>
                
              </div>
            </div>
            <!-- Stats Chips -->
            <div class="px-3 pb-3 space-y-2">
              <div class="grid grid-cols-3 gap-1">
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  sta: {{ villager.stats?.stamina || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  str: {{ villager.stats?.str || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  agi: {{ villager.stats?.agi || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  int: {{ villager.stats?.int || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  luck: {{ villager.stats?.luck || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  def: {{ villager.stats?.defense || 0 }}
                </span>
              </div>
            </div>
            
            <div class="px-3 pb-3 text-xs text-gray-400 truncate">
                  เพิ่มเมื่อ: {{ formatDate(villager.createdAt) }}
                </div>
            <!-- Action Footer (Edit if Admin) -->
            <div v-if="isAdmin" class="px-3 pb-3">
              <button 
                @click.stop="openEditModal(villager)" 
                class="w-full px-2 py-1 text-white bg-indigo-700 hover:bg-indigo-600 rounded text-xs transition-colors duration-200 flex items-center justify-center gap-2"
                aria-label="แก้ไขชาวบ้าน"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
            <!-- Hover Highlight -->
            <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
          </div>
        </div>
        <!-- Load More Button for Recent Villagers -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMoreRecent && !isRecentLoading" 
            @click="loadMoreRecent" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดชาวบ้านเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isRecentLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMoreRecent && displayedRecentVillagers.length > 0" class="text-indigo-200 text-sm">ไม่มีชาวบ้านเพิ่มเติมแล้ว</span>
        </div>
      </div>
    </section>

    <!-- Main Content for Filter Tab -->
    <main v-if="activeTab === 'filter'" class="max-w-7xl mx-auto">
      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Prompt to Select Location -->
      <div v-if="!filterLocation" class="text-center text-gray-300 py-10">
        <div class="mx-auto w-16 h-16 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-3">
          <i class="fas fa-map-marker-alt text-gray-500"></i>
        </div>
        <div class="text-lg text-gray-200 mb-2">กรุณาเลือกสถานที่</div>
        <div class="text-sm text-gray-400 mb-4">เพื่อแสดงรายการชาวบ้านในพื้นที่นั้น</div>
      </div>

      <div v-else>
        <!-- No Villagers Message -->
        <div v-if="displayedVillagers.length === 0 && !isLoading && filterLocation" class="text-center text-gray-300 py-10">
          <div class="mx-auto w-16 h-16 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-3">
            <i class="fas fa-users-slash text-gray-500"></i>
          </div>
          <div class="text-lg text-gray-200 mb-2">ไม่พบชาวบ้านที่ตรงกับการค้นหา</div>
          <div class="text-sm text-gray-400 mb-4">ลองปรับตัวกรองหรือค้นหาชื่ออื่น</div>
        </div>
        <!-- Grid of Villagers -->
        <div v-else class="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          <div 
            v-for="villager in displayedVillagers" 
            :key="villager.id" 
            class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition cursor-pointer"
            @click="openDetailsModal(villager)"
          >
            <!-- Header: Image + Name + Level -->
            <div class="p-3 flex items-center">
              <div class="relative">
                <img 
                  :src="villager.img || 'default-villager.png'" 
                  alt="Villager Image" 
                  class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                  @error="handleImageError"
                />
                <div 
                  v-if="villager.level"
                  class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                >
                  {{ villager.level }}
                </div>
              </div>
              <div class="ml-3 flex-1">
                <div class="flex items-center justify-between">
                  <div class="font-semibold text-gray-100 truncate">
                    {{ villager.name }} ({{ (villager.chance / 100).toFixed(2) }}%)
                  </div>
                </div>
                <div class="flex items-center space-x-1 px-1">
                  <!-- ชิปบุก -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                    <i class="fas fa-dice text-red-300 text-xs"></i>
                    <span class="font-mono">{{ getDice(villager, 'attackDice') }}</span>
                  </div>
                  <!-- ชิปตั้งรับ -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                    <i class="fas fa-dice text-blue-300 text-xs"></i>
                    <span class="font-mono">{{ getDice(villager, 'defenseDice') }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Stats Chips -->
            <div class="px-3 pb-3 space-y-2">
              <div class="grid grid-cols-3 gap-1">
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  sta: {{ villager.stats?.stamina || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  str: {{ villager.stats?.str || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  agi: {{ villager.stats?.agi || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  int: {{ villager.stats?.int || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  luck: {{ villager.stats?.luck || 0 }}
                </span>
                <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                  def: {{ villager.stats?.defense || 0 }}
                </span>
              </div>
            </div>
            
            <!-- Action Footer (Edit if Admin) -->
            <div v-if="isAdmin" class="px-3 pb-3">
              <button 
                @click.stop="openEditModal(villager)" 
                class="w-full px-2 py-1 text-white bg-indigo-700 hover:bg-indigo-600 rounded text-xs transition-colors duration-200 flex items-center justify-center gap-2"
                aria-label="แก้ไขชาวบ้าน"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
            <!-- Hover Highlight -->
            <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
          </div>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMore && !isLoading" 
            @click="loadMore" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMore && displayedVillagers.length > 0" class="text-indigo-200 text-sm">ไม่มีชาวบ้านเพิ่มเติมแล้ว</span>
        </div>
      </div>
    </main>

    <!-- Modals -->
    <AddVillagerModal 
      v-if="toggleModal === 'AddVillagerModal'" 
      @cancelModal="toggleModal = ''" 
      @villagerAdded="resetAndFetch" 
    />
    <EditVillagerModal 
      v-if="toggleModal === 'EditVillagerModal'" 
      :villager-id="selectedVillagerId" 
      @cancelModal="toggleModal = ''" 
      @villagerUpdated="resetAndFetch" 
    />
    <DetailVillagerModal 
      v-if="toggleModal === 'DetailVillagerModal'" 
      :villager-id="selectedVillagerId" 
      @cancelModal="toggleModal = ''" 
      @editVillager="openEditModalFromDetail"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import AddVillagerModal from '../components/admin/AddVillagerModal.vue';
import EditVillagerModal from '../components/admin/EditVillagerModal.vue';
import DetailVillagerModal from '../components/admin/DetailVillagerModal.vue';
import TemplateExportButton from '../components/admin/TemplateExportButton.vue';
import gameVillagersData from '@/data/gameVillagers.json';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  components: { AddVillagerModal, EditVillagerModal, DetailVillagerModal, TemplateExportButton },
  setup() {
    const toggleModal = ref('');
    const villagers = ref([]);
    const recentVillagers = ref([]);
    const filterName = ref('');
    const filterLocation = ref('');
    const filterType = ref('');
    const filterRecentType = ref('');
    const filterRecentName = ref('');
    const selectedVillagerId = ref(null);
    const isLoading = ref(false);
    const isRecentLoading = ref(false);
    const isAdmin = ref(false);
    const error = ref('');
    const villagersPerLoad = 10;
    const currentPage = ref(1);
    const maps = ref([]);
    const activeTab = ref('filter');
    const lastRecentDoc = ref(null);
    const hasMoreRecent = ref(true);

    // Define the custom order for locations (maps)
    const customMapOrder = [
      'ตรอกซอย', 'ตลาด', 'สนามกอล์ฟ', 'โรงเรียน', 'มหาวิทยาลัย', 'โกดัง', 'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
      'สถานีตำรวจ', 'หอสมุดแห่งชาติ', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร','สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส', 'พระบรมมหาราชวัง', 'ป่าช้า', 'สุสานนักรบโบราณ'
    ];

    const locations = computed(() => {
      return maps.value
        .map(m => m.name)
        .filter(name => typeof name === 'string')
        .sort((a, b) => {
          const indexA = customMapOrder.indexOf(a);
          const indexB = customMapOrder.indexOf(b);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.localeCompare(b);
        });
    });

    const { user } = getUser();

    // Debounce function for name search
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    };

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-villager.png';
    };

    // Format date for display
    const formatDate = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    // Fetch maps to get locations
    const fetchMaps = async () => {
      try {
        error.value = null;
        let mapsQuery = projectFirestore.collection('maps');
        if (!isAdmin.value) {
          mapsQuery = mapsQuery.where('isActive', '==', true);
        }
        const mapsSnapshot = await mapsQuery.get();

        maps.value = mapsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: typeof data.name === 'string' ? data.name : 'Unknown',
            isActive: data.isActive !== undefined ? data.isActive : true,
            ...data
          };
        });
      } catch (err) {
        error.value = 'ไม่สามารถดึงข้อมูลแผนที่ได้';
        console.error('Error fetching maps:', err);
      }
    };

    const getTime = (createdAt) => {
      if (!createdAt) return 0;
      if (createdAt.toDate) return createdAt.toDate().getTime();
      if (createdAt.seconds) return createdAt.seconds * 1000;
      return new Date(createdAt).getTime();
    };

    // Fetch villagers from JSON with filters
    const fetchVillagers = async () => {
      if (!filterLocation.value) {
        villagers.value = [];
        isLoading.value = false;
        return;
      }

      try {
        isLoading.value = true;
        error.value = '';
        const mapQuery = projectFirestore.collection('maps')
          .where('name', '==', filterLocation.value)
          .limit(1);

        const mapSnapshot = await mapQuery.get();

        if (mapSnapshot.empty) {
          villagers.value = [];
          isLoading.value = false;
          return;
        }

        const mapData = mapSnapshot.docs[0].data();
        const mapVillagers = mapData.villagers || [];

        let newVillagers = mapVillagers.map(v => {
          const data = gameVillagersData[v.name];
          if (!data) return null;
          if (filterType.value && data.type !== filterType.value) {
            return null;
          }
          return { id: v.name, ...data, chance: v.chance };
        }).filter(v => v !== null);

        if (filterName.value) {
          newVillagers = newVillagers.filter(villager =>
            villager.name.toLowerCase().includes(filterName.value.toLowerCase())
          );
        }

        // Sort by chance descending
        newVillagers.sort((a, b) => b.chance - a.chance);

        villagers.value = newVillagers;
        currentPage.value = 1;
      } catch (error) {
        console.error('Error fetching villagers:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลชาวบ้าน';
      } finally {
        isLoading.value = false;
      }
    };

    // Fetch recent villagers from JSON with pagination
    const fetchRecentVillagers = async (append = false) => {
      try {
        isRecentLoading.value = true;
        error.value = '';

        let newVillagers = Object.entries(gameVillagersData)
          .map(([id, data]) => ({
            id,
            ...data,
            createdAt: data.createdAt || null
          }))
          .filter(villager => villager.createdAt);

        if (filterRecentType.value) {
          newVillagers = newVillagers.filter(v => v.type === filterRecentType.value);
        }

        newVillagers.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

        if (!append) {
          recentVillagers.value = [];
          lastRecentDoc.value = null;
        }

        if (!append) {
          recentVillagers.value = newVillagers;
        }

        hasMoreRecent.value = false;
      } catch (error) {
        console.error('Error fetching recent villagers:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลชาวบ้านล่าสุด';
      } finally {
        isRecentLoading.value = false;
      }
    };

    // Compute displayed villagers for pagination
    const displayedVillagers = computed(() => {
      const end = currentPage.value * villagersPerLoad;
      return villagers.value.slice(0, end);
    });

    const displayedRecentVillagers = computed(() => {
      let filtered = recentVillagers.value;
      if (filterRecentName.value) {
        filtered = filtered.filter(v => v.name.toLowerCase().includes(filterRecentName.value.toLowerCase()));
      }
      return filtered;
    });

    const hasMore = computed(() => {
      return (currentPage.value * villagersPerLoad) < villagers.value.length;
    });

    // Apply filters and reset
    const applyFilters = () => {
      villagers.value = [];
      currentPage.value = 1;
      fetchVillagers();
    };

    // Debounced version of applyFilters for name search
    const debouncedApplyFilters = debounce(applyFilters, 300);

    const applyRecentFilters = () => {
      recentVillagers.value = [];
      lastRecentDoc.value = null;
      hasMoreRecent.value = true;
      fetchRecentVillagers();
    };

    // Load more villagers
    const loadMore = () => {
      currentPage.value += 1;
    };

    const loadMoreRecent = () => {
      fetchRecentVillagers(true);
    };

    // Reset and fetch villagers
    const resetAndFetch = () => {
      villagers.value = [];
      currentPage.value = 1;
      recentVillagers.value = [];
      lastRecentDoc.value = null;
      hasMoreRecent.value = true;
      filterRecentType.value = '';
      filterRecentName.value = '';
      fetchVillagers();
      if (activeTab.value === 'recent') {
        fetchRecentVillagers();
      }
    };

    // Set active tab and fetch data if necessary
    const setActiveTab = (tab) => {
      activeTab.value = tab;
      if (tab === 'recent') {
        recentVillagers.value = [];
        lastRecentDoc.value = null;
        hasMoreRecent.value = true;
        fetchRecentVillagers();
      }
    };

    // Open edit modal
    const openEditModal = (villager) => {
      selectedVillagerId.value = villager.id;
      toggleModal.value = 'EditVillagerModal';
    };

    // Open details modal
    const openDetailsModal = (villager) => {
      selectedVillagerId.value = villager.id;
      toggleModal.value = 'DetailVillagerModal';
    };

    // Open edit from detail
    const openEditModalFromDetail = (villagerId) => {
      selectedVillagerId.value = villagerId;
      toggleModal.value = 'EditVillagerModal';
    };

    const getDice = (villager, key) => {
      return villager[key] || 0;
    };

    // Fetch maps and recent villagers on mount
    onMounted(async () => {
      await fetchMaps();
      if (isAdmin.value && activeTab.value === 'recent') {
        await fetchRecentVillagers();
      }
    });

    return { 
      toggleModal, 
      filterName, 
      filterLocation, 
      filterType,
      filterRecentType,
      filterRecentName,
      villagers, 
      recentVillagers,
      displayedVillagers, 
      displayedRecentVillagers,
      selectedVillagerId, 
      openEditModal, 
      openDetailsModal, 
      openEditModalFromDetail,
      loadMore, 
      loadMoreRecent,
      hasMore, 
      hasMoreRecent,
      isLoading, 
      isRecentLoading,
      isAdmin, 
      resetAndFetch, 
      applyFilters, 
      debouncedApplyFilters, 
      applyRecentFilters,
      error, 
      handleImageError,
      locations,
      activeTab,
      formatDate,
      setActiveTab,
      getDice
    };
  },
};
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
</style>
